<script setup lang="ts">
import {ref, onMounted, computed} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import type {BadgeHistoryItemDTO} from '@/ts/types/dto/badge-dto';
import {BadgeFilter, BadgeSort} from '@/ts/types/component/badge-filter';
import {getLatestTimestamp, getEarliestTimestamp} from '@/ts/utils/pure-functions';
import BadgeCard from '@/vue/molecules/badge-card.vue';
import BadgeStatsCard from '@/vue/molecules/badge-stats-card.vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const api = useApi();

const badges = ref<BadgeHistoryItemDTO[] | null>(null);
const loadingBadges = ref(true);
const errorBadges = ref<string | null>(null);

const selectedFilter = ref<BadgeFilter>(BadgeFilter.ALL);
const selectedSort = ref<BadgeSort>(BadgeSort.MOST_RECENT);

interface Opt<T> { label: string; value: T }

const filterOptions = computed<Opt<BadgeFilter>[]>(() => [
  {label: t('all_badges'), value: BadgeFilter.ALL},
  {label: t('earned_badges'), value: BadgeFilter.EARNED},
  {label: t('not_earned_badges'), value: BadgeFilter.NOT_EARNED},
]);

const sortOptions = computed<Opt<BadgeSort>[]>(() => [
  {label: t('sort_most_recent'), value: BadgeSort.MOST_RECENT},
  {label: t('sort_oldest_first'), value: BadgeSort.OLDEST_FIRST},
  {label: t('sort_name_az'), value: BadgeSort.NAME_AZ},
  {label: t('sort_most_earned'), value: BadgeSort.MOST_EARNED},
]);

const totalBadgesEarned = computed(() => {
  if (!badges.value) {return 0;}
  return badges.value.filter(badge => badge.earnedTimes.length > 0).length;
});

const totalTimesEarned = computed(() => {
  if (!badges.value) {return 0;}
  return badges.value.reduce((sum, badge) => sum + badge.earnedTimes.length, 0);
});

const filteredAndSortedBadges = computed(() => {
  if (!badges.value) {return [];}

  let result = [...badges.value];

  if (selectedFilter.value === BadgeFilter.EARNED) {
    result = result.filter(b => b.earnedTimes.length > 0);
  } else if (selectedFilter.value === BadgeFilter.NOT_EARNED) {
    result = result.filter(b => b.earnedTimes.length === 0);
  }

  switch (selectedSort.value) {
    case BadgeSort.MOST_RECENT:
      result.sort((a, b) => getLatestTimestamp(b.earnedTimes) - getLatestTimestamp(a.earnedTimes));
      break;
    case BadgeSort.OLDEST_FIRST:
      result.sort((a, b) => {
        const aKey = getEarliestTimestamp(a.earnedTimes) || Number.POSITIVE_INFINITY;
        const bKey = getEarliestTimestamp(b.earnedTimes) || Number.POSITIVE_INFINITY;
        return aKey - bKey;
      });
      break;
    case BadgeSort.NAME_AZ:
      result.sort((a, b) => a.badge.title.localeCompare(b.badge.title));
      break;
    case BadgeSort.MOST_EARNED:
      result.sort((a, b) => b.earnedTimes.length - a.earnedTimes.length);
      break;
    default:
      break;
  }

  return result;
});

onMounted(async() => {
  try {
    const response = await api.content.getBadges();
    badges.value = response.data as BadgeHistoryItemDTO[];
  } catch (_err) {
    errorBadges.value = 'fetch_failed';
  } finally {
    loadingBadges.value = false;
  }
});
</script>

<template>
  <div class="account-tab-page">
    <h3 class="info-title">
      {{ $t('my_badges') }}
    </h3>

    <div v-if="loadingBadges" class="skeleton-container">
      <!-- Stats header skeleton -->
      <div class="stats-header">
        <q-skeleton class="skeleton-stats-card"/>
        <q-skeleton class="skeleton-stats-card"/>
      </div>

      <!-- Filter section skeleton -->
      <div class="filter-section">
        <q-skeleton class="skeleton-select"/>
        <q-skeleton class="skeleton-select"/>
      </div>

      <!-- Badges grid skeleton -->
      <div class="badges-grid">
        <q-skeleton v-for="n in 6" :key="n" class="skeleton-badge-card"/>
      </div>
    </div>

    <div v-else-if="errorBadges" class="error-container">
      <p class="error-message">
        {{ $t('error_loading_badges') }}
      </p>
    </div>

    <div v-else-if="badges && badges.length === 0" class="empty-container">
      <p class="empty-message">
        {{ $t('no_badges_yet') }}
      </p>
    </div>

    <div v-else>
      <div class="stats-header">
        <badge-stats-card :title="$t('total_badges')" :value="totalBadgesEarned"/>
        <badge-stats-card :title="$t('times_earned')" :value="totalTimesEarned"/>
      </div>

      <div class="filter-section">
        <q-select
          v-model="selectedFilter"
          :options="filterOptions"
          option-value="value"
          option-label="label"
          emit-value
          map-options
          borderless
          dense
          class="filter-select"
        />
        <q-select
          v-model="selectedSort"
          :options="sortOptions"
          option-value="value"
          option-label="label"
          emit-value
          map-options
          borderless
          dense
          class="sort-select"
        />
      </div>

      <div class="badges-grid">
        <badge-card
          v-for="badge in filteredAndSortedBadges"
          :key="badge.badge.id"
          :item="badge"
        />
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">
.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);
}

.info-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-5);
}

.error-container,
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--Spacing-spacing-10) var(--Spacing-spacing-5);
  min-height: 200px;
}

.skeleton-container {
  display: flex;
  flex-direction: column;
}

.skeleton-stats-card {
  height: 180px;
  border-radius: var(--card-border-radius);
}

.skeleton-select {
  width: 140px;
  height: 40px;
  border-radius: var(--card-border-radius);
}

.skeleton-badge-card {
  height: 120px;
  border-radius: var(--card-border-radius);
}

.error-message,
.empty-message {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-80);
  text-align: center;
  margin: 0;
}

.stats-header {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: var(--Spacing-spacing-3);
  margin-top: var(--Spacing-spacing-5);
  margin-bottom: var(--Spacing-spacing-6);

  @include screen-sm {
    grid-template-columns: repeat(2, 1fr);
  }
}

.filter-section {
  display: flex;
  gap: var(--Spacing-spacing-4);
  flex-wrap: wrap;
  margin-top: var(--Spacing-spacing-6);
  margin-bottom: var(--Spacing-spacing-6);

  .filter-select,
  .sort-select {
    flex: 0 0 auto;
    width: auto;
  }
}

.badges-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: var(--Spacing-spacing-4);


  @include screen-md {
    grid-template-columns: repeat(2, 1fr);
  }

  @include screen-lg {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
